<template>
  <div>
    <v-row
      dense
      class="mb-2"
    >
      <v-col
        cols="12"
        sm="9"
      >
        <v-text-field
          v-model="search"
          label="Buscar"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          clearable
          hide-details
          @keyup.enter="reload(true)"
          @click:clear="onClear"
        />
      </v-col>
      <v-col
        cols="12"
        sm="3"
        class="d-flex align-center justify-end"
      >
        <v-btn
          color="secondary"
          small
          block
          @click="openCreate"
        >
          <v-icon
            left
            small
          >
            mdi-plus
          </v-icon>
          Novo
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :headers="headers"
      :items="items"
      :server-items-length="total"
      :options.sync="options"
      :loading="loading"
      :footer-props="{ 'items-per-page-options': [10, 25, 50] }"
      item-key="id"
      dense
    >
      <template #item.actions="{ item }">
        <v-btn
          icon
          small
          @click="openEdit(item)"
        >
          <v-icon small>
            mdi-pencil
          </v-icon>
        </v-btn>
        <v-btn
          icon
          small
          color="error"
          @click="remove(item)"
        >
          <v-icon small>
            mdi-delete
          </v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog
      v-model="dialog.show"
      max-width="420"
      persistent
    >
      <v-card>
        <v-card-title>{{ dialog.mode === 'create' ? 'Novo conselho' : 'Editar conselho' }}</v-card-title>
        <v-card-text>
          <v-text-field
            v-model="dialog.form.sigla"
            label="Sigla"
            outlined
            dense
            maxlength="20"
          />
          <v-text-field
            v-model="dialog.form.descricao"
            label="Descrição"
            outlined
            dense
            class="mt-2"
          />
          <v-alert
            v-if="dialog.error"
            type="error"
            dense
            outlined
            border="left"
          >
            {{ dialog.error }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            :disabled="dialog.saving"
            @click="dialog.show = false"
          >
            Cancelar
          </v-btn>
          <v-btn
            color="secondary"
            :loading="dialog.saving"
            @click="submit"
          >
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
    >
      {{ snackbar.text }}
    </v-snackbar>
  </div>
</template>

<script>
  import { $k } from '@/lib/kraken'

  export default {
    name: 'ConselhosTab',

    props: {
      active: { type: Boolean, default: false },
    },

    data: () => ({
      search: '',
      items: [],
      total: 0,
      options: { page: 1, itemsPerPage: 25 },
      loading: false,
      hasLoaded: false,
      headers: [
        { text: 'ID', value: 'id', width: 80 },
        { text: 'Sigla', value: 'sigla', width: 120 },
        { text: 'Descrição', value: 'descricao' },
        { text: '', value: 'actions', width: 110, sortable: false, align: 'end' },
      ],
      dialog: {
        show: false,
        mode: 'create',
        saving: false,
        error: '',
        editingId: null,
        form: { sigla: '', descricao: '' },
      },
      snackbar: { show: false, color: 'success', text: '' },
    }),

    watch: {
      options: {
        deep: true,
        handler () {
          if (this.active || this.hasLoaded) this.reload()
        },
      },
      active (val) {
        if (val && !this.hasLoaded) this.reload(true)
      },
    },

    mounted () {
      if (this.active) this.reload(true)
    },

    methods: {
      msgFromError (err, fallback) {
        return err?.response?.data?.messages?.error?.[0] ||
          err?.response?.data?.messages?.[0]?.text ||
          err?.message ||
          fallback
      },
      onClear () {
        this.search = ''
        this.reload(true)
      },
      async reload (resetPage = false) {
        if (resetPage && this.options.page !== 1) {
          this.options = { ...this.options, page: 1 }
          return
        }
        this.loading = true
        try {
          const skip = (this.options.page - 1) * this.options.itemsPerPage
          const payload = { skip, limit: this.options.itemsPerPage }
          if (this.search) payload.q = this.search
          const res = await $k.manage.clinic_one.dictionaries.conselhos.get(payload)
          this.items = res.results || []
          this.total = res.total || 0
          this.hasLoaded = true
        } catch (err) {
          console.error('[conselhos.get]', err)
        } finally {
          this.loading = false
        }
      },
      openCreate () {
        this.dialog = {
          show: true,
          mode: 'create',
          saving: false,
          error: '',
          editingId: null,
          form: { sigla: '', descricao: '' },
        }
      },
      openEdit (item) {
        this.dialog = {
          show: true,
          mode: 'edit',
          saving: false,
          error: '',
          editingId: item.id,
          form: { sigla: item.sigla, descricao: item.descricao },
        }
      },
      async submit () {
        const { mode, form, editingId } = this.dialog
        if (!form.sigla?.trim() || !form.descricao?.trim()) {
          this.dialog.error = 'Sigla e descrição são obrigatórias.'
          return
        }
        this.dialog.saving = true
        this.dialog.error = ''
        try {
          const body = { sigla: form.sigla.trim(), descricao: form.descricao.trim() }
          if (mode === 'create') {
            await $k.manage.clinic_one.dictionaries.conselhos.post(body)
          } else {
            await $k.manage.clinic_one.dictionaries.conselhos._id.put({ id: editingId, ...body })
          }
          this.snackbar = { show: true, color: 'success', text: 'Salvo.' }
          this.dialog.show = false
          await this.reload()
        } catch (err) {
          console.error('[conselhos.submit]', err)
          this.dialog.error = this.msgFromError(err, 'Falha ao salvar.')
        } finally {
          this.dialog.saving = false
        }
      },
      async remove (item) {
        if (!confirm(`Excluir "${item.sigla}"?`)) return
        try {
          await $k.manage.clinic_one.dictionaries.conselhos._id.delete({ id: item.id })
          this.snackbar = { show: true, color: 'success', text: 'Excluído.' }
          await this.reload()
        } catch (err) {
          console.error('[conselhos.delete]', err)
          this.snackbar = { show: true, color: 'error', text: this.msgFromError(err, 'Falha ao excluir.') }
        }
      },
    },
  }
</script>
